import React, { useRef, useState } from "react";
import { Box, Dialog, Divider, Typography, useTheme } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { RotateCcw, TriangleAlert } from "lucide-react";
import SuccessDialogCst from "@components/SuccessDialogCst";
import { openToast } from "@utils/toastStore";
import { onErrorMessage } from "@utils/utilsApi";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { EnrolledUserItem } from "../types";
import {
  getIssuedCertificateIds,
  getRefreshOutcome,
  getRefreshResultLines,
  RefreshOutcome,
  useRefreshCertificates,
} from "../hooks/useRefreshCertificates";

export type RefreshCertificatesTarget =
  { scope: "all" } | { scope: "user"; user: EnrolledUserItem; name: string };

interface RefreshCertificatesDialogProps {
  target: RefreshCertificatesTarget | null;
  corporateId: string | number;
  objectId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const key = (suffix: string) => `card.refresh-certificates.${suffix}`;

const RefreshCertificatesDialog: React.FC<RefreshCertificatesDialogProps> = ({
  target,
  corporateId,
  objectId,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const { refreshAll, refreshUser, isMutating } = useRefreshCertificates();
  const [outcome, setOutcome] = useState<RefreshOutcome | null>(null);
  const lastTarget = useRef<RefreshCertificatesTarget | null>(null);

  if (target) lastTarget.current = target;
  const current = target ?? lastTarget.current;
  const isUser = current?.scope === "user";
  const name = current?.scope === "user" ? current.name : "";

  const close = () => {
    onClose();
    setOutcome(null);
  };

  const confirm = async () => {
    if (!target) return;
    if (target.scope === "user" && !target.user.userId) return;
    try {
      const result =
        target.scope === "user"
          ? await refreshUser({
              corporateId,
              objectId,
              learnerId: target.user.userId as string | number,
              issuedCertificateIds: getIssuedCertificateIds(target.user),
            })
          : await refreshAll({ corporateId, objectId });
      const nextOutcome = getRefreshOutcome(result);
      if (nextOutcome.updated > 0) onSuccess?.();
      setOutcome(nextOutcome);
    } catch (error: any) {
      console.error(error);
      const apiMessage = onErrorMessage(error);
      openToast({
        severity: "error",
        message:
          error?.response?.data?.message ||
          (apiMessage !== error?.message && apiMessage) ||
          String(t(key("error"))),
      });
    }
  };

  const lines = outcome ? getRefreshResultLines(outcome) : [];
  const nothingUpdated =
    outcome?.kind === "none-updated" ||
    (outcome?.kind === "partial" && outcome.updated === 0);
  const showNameText = !!outcome && outcome.updated > 0;
  const resultTitle = nothingUpdated
    ? "none-updated-title"
    : outcome?.kind === "started"
      ? "started-title"
      : outcome?.kind === "updated"
        ? "success-title"
        : "completed-title";

  const titleSx = {
    fontSize: "24px",
    fontWeight: 700,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
    overflowWrap: "anywhere",
  };
  const textSx = {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  };

  return (
    <>
      <Dialog
        open={!!target && !outcome}
        onClose={isMutating ? undefined : onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: "600px",
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "16px",
            padding: "32px",
            color: theme.palette.text.primary,
          }}
        >
          <RotateCcw size={40} />
          <Typography sx={titleSx}>
            {isUser
              ? t(key("title-user"), {
                  name,
                  interpolation: { escapeValue: false },
                })
              : t(key("title-all"))}
          </Typography>
          <Typography sx={textSx}>
            {t(isUser ? key("description-user") : key("description-all"))}
          </Typography>
          <Divider flexItem />
          <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
            <ButtonSecondary
              size="md"
              onClick={onClose}
              disabled={isMutating}
              sx={{ flex: 1 }}
            >
              {t("card.delete-confirm.cancel")}
            </ButtonSecondary>
            <ButtonPrimary
              size="md"
              onClick={confirm}
              disabled={isMutating}
              sx={{ flex: 1 }}
            >
              {t(key("confirm"))}
            </ButtonPrimary>
          </Box>
        </Box>
      </Dialog>
      <SuccessDialogCst
        open={!!target && !!outcome}
        icon={nothingUpdated ? <TriangleAlert size={40} /> : undefined}
        title={String(t(key(resultTitle)))}
        description={
          showNameText
            ? String(
                isUser
                  ? t(key("success-user"), {
                      name,
                      interpolation: { escapeValue: false },
                    })
                  : t(key("success-all")),
              )
            : undefined
        }
        onClose={close}
        showClose
      >
        {lines.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {lines.map((line, index) => (
              <Typography key={line.key} sx={textSx}>
                <Trans
                  t={t}
                  i18nKey={key(line.key)}
                  count={line.count}
                  components={{ strong: <strong /> }}
                />
                {index < lines.length - 1 ? ";" : "."}
              </Typography>
            ))}
          </Box>
        )}
      </SuccessDialogCst>
    </>
  );
};

export default RefreshCertificatesDialog;
